// Shared admin portal UI kit — sharp-cornered, forest/bone/blaze aesthetic.
import { createContext, useContext } from "react";

export const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

export const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
export const money2 = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const OVERLINE = "text-[11px] font-serif uppercase tracking-[0.15em] font-bold text-[#5C6B61]";
export const H1 = "text-3xl md:text-4xl font-serif tracking-tight text-[#1F2A22]";

const STATUS_MAP = {
  paid: "bg-[#1F2A22]/10 text-[#1F2A22]", active: "bg-[#1F2A22]/10 text-[#1F2A22]", approved: "bg-[#1F2A22]/10 text-[#1F2A22]",
  completed: "bg-[#1F2A22]/10 text-[#1F2A22]", paid_partner: "bg-[#1F2A22]/10 text-[#1F2A22]", founding_state: "bg-[#1F2A22]/10 text-[#1F2A22]",
  pending: "bg-[#FF6A00]/10 text-[#FF6A00]", pending_review: "bg-[#FF6A00]/10 text-[#FF6A00]", trial: "bg-[#FF6A00]/10 text-[#FF6A00]",
  initiated: "bg-[#FF6A00]/10 text-[#FF6A00]", past_due: "bg-[#FF6A00]/10 text-[#FF6A00]", needs_changes: "bg-[#FF6A00]/10 text-[#FF6A00]",
  refunded: "bg-[#8A5A44]/15 text-[#8A5A44]", disputed: "bg-red-100 text-red-800", failed: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800", cancelled: "bg-red-100 text-red-800", canceled: "bg-red-100 text-red-800",
  published: "bg-[#2E7D46]/15 text-[#2E7D46]", draft: "bg-[#E5E2DC] text-[#5C6B61]", scheduled: "bg-[#8A5A44]/15 text-[#8A5A44]",
};

export function StatusBadge({ status }) {
  const cls = STATUS_MAP[status] || "bg-[#E5E2DC] text-[#5C6B61]";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`} data-testid={`status-${status}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, testid, accent }) {
  return (
    <div className="border border-[#E5E2DC] bg-white p-6 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <div className="flex items-center justify-between pb-2">
        <span className={OVERLINE}>{label}</span>
        {Icon && <Icon size={16} className={accent ? "text-[#FF6A00]" : "text-[#8A5A44]"} />}
      </div>
      <div className="text-3xl font-serif text-[#1F2A22] tabular-nums">{value}</div>
      {sub != null && <div className="text-xs text-[#5C6B61] mt-1">{sub}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", ...rest }) {
  return (
    <div className={`border border-[#E5E2DC] bg-white ${className}`} {...rest}>
      {(title || action) && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
          <h3 className="text-lg font-serif text-[#1F2A22]">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-6">{children}</div>
    </div>
  );
}

export const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-none bg-[#FF6A00] px-4 py-2 text-sm font-semibold text-white hover:bg-[#E65F00] transition-colors disabled:opacity-50";
export const btnSecondary = "inline-flex items-center justify-center gap-2 rounded-none border border-[#1F2A22] bg-transparent px-4 py-2 text-sm font-semibold text-[#1F2A22] hover:bg-[#1F2A22] hover:text-[#F9F8F6] transition-colors disabled:opacity-50";
